import React, { useEffect, useState, type JSX } from "react";
import { useDrag, useDrop } from "react-dnd";

interface Item {
  id: string;
  index: number;
}

export interface DragableItemProps {
  itemType?: string;
  id: string | number;
  index: number;
  className?: string;
  onItemMove?: (id: string, atIndex: number) => void;
  children: (dragRef: (el: HTMLElement) => void) => JSX.Element;
}

export function DragableCell({ itemType = "", id, index, className, children, onItemMove }: DragableItemProps) {
  const [rowRef, setRowRef] = useState<HTMLElement | null>();

  const [{ isDragging }, drag, preview] = useDrag(
    () => ({
      type: itemType,
      item: { id, index },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
      end: (item, monitor) => {
        const { id: droppedId, index: originalIndex } = item;
        const didDrop = monitor.didDrop();

        if (!didDrop) {
          onItemMove?.(droppedId as string, originalIndex);
        }
      },
    }),
    [itemType, id, index, onItemMove]
  );

  const [, drop] = useDrop(
    () => ({
      accept: itemType,
      hover({ id: draggedId }: Item) {
        if (draggedId !== id) {
          onItemMove?.(draggedId, index);
        }
      },
    }),
    [itemType, id, index, onItemMove]
  );

  useEffect(() => {
    if (!rowRef) return;

    if (isDragging) {
      rowRef.style.opacity = "0";
    } else {
      rowRef.style.opacity = "1";
    }
  }, [isDragging, rowRef]);

  return (
    <div
      ref={node => {
        setRowRef(node?.parentElement?.parentElement);
        preview(drop(node?.parentElement?.parentElement ?? node));
      }}
      className={className}
      style={{ transform: "translate(0, 0)" }}
    >
      {children(drag)}
    </div>
  );
}
